import { create } from 'zustand';
import { useAppStore } from './useAppStore';
import { notificationAPI, type Notification } from '../api/notifications';

export function notificationSession(): string {
  if (useAppStore.getState().isLoggedOut || !localStorage.getItem('access_token')) return '';
  try {
    const user = JSON.parse(localStorage.getItem('user_info') || '{}');
    return String(user.id || localStorage.getItem('access_token'));
  } catch { return localStorage.getItem('access_token') || ''; }
}
interface State {
  owner: string; pending: Notification[]; loading: boolean; error: string; version: number;
  downloadsOpen: boolean; detailPreview: boolean; centerOpen: boolean; detail: Notification | null;
  compose: Notification | null | undefined;
  syncSession: () => void; refresh: () => Promise<void>;
  read: (id: string, method?: 'auto_display' | 'manual_ack' | 'bulk_manual') => Promise<void>;
}
let inFlight: Promise<void> | null = null;
let flightOwner = '';
let revision = 0;
export const useNotificationStore = create<State>((set, get) => ({
  owner: '', pending: [], loading: false, error: '', version: 0,
  downloadsOpen: false, detailPreview: false, centerOpen: false, detail: null, compose: undefined,
  syncSession: () => {
    const owner = notificationSession();
    if (owner !== get().owner) {
      revision++;
      set({ owner, pending: [], loading: false, error: '', centerOpen: false, detail: null, compose: undefined });
    }
  },
  refresh: async () => {
    get().syncSession();
    const owner = get().owner;
    if (!owner) return;
    if (inFlight && flightOwner === owner) return inFlight;
    flightOwner = owner;
    const startRevision = revision;
    set({ loading: true });
    const task = (async () => {
      try {
        const all: Notification[] = [];
        let cursor = '';
        for (;;) {
          const data = await notificationAPI.pending(cursor);
          if (notificationSession() !== owner || get().owner !== owner) return;
          all.push(...data.list);
          if (data.list.length < 100) break;
          const next = data.list[data.list.length - 1].id;
          if (next === cursor) throw new Error('通知加载异常，请重试');
          cursor = next;
        }
        if (revision === startRevision) set({ pending: all, error: '' });
      } catch (e) {
        if (get().owner === owner) set({ error: e instanceof Error ? e.message : '通知加载失败' });
      } finally {
        if (get().owner === owner) set({ loading: false });
      }
    })();
    inFlight = task;
    try { await task; } finally { if (inFlight === task) inFlight = null; }
  },
  read: async (id, method = 'manual_ack') => {
    get().syncSession();
    const owner = get().owner;
    if (!owner) throw new Error('请先登录');
    await notificationAPI.read(id, method);
    if (notificationSession() !== owner || get().owner !== owner) return;
    revision++;
    set(s => ({
      pending: s.pending.filter(n => n.id !== id), version: s.version + 1,
      detail: s.detail?.id === id ? { ...s.detail, read_at: new Date().toISOString() } : s.detail,
    }));
  },
}));
